'use client';

import {
  formatLocalIsoDate,
  parseIsoDate,
} from '@hyphengroup/cms-components/utils/helpers';
import { differenceInCalendarDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  CmsCountry,
  CmsRegion,
  TravelInsuranceType,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/types';
import { TravelCompareQuotesParams } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/types';
import { CoverTypeToggle } from '../CoverTypeToggle/CoverTypeToggle';
import { FamilyTravelModal } from './FamilyTravelModal';
import Translations from './i18n';
import {
  trackTiDateSelectionConfirm,
  trackTiDateSelectionStart,
  trackTiDateUnavailableClick,
  trackTripDetailsCoverType,
  trackTripDetailsDateChange,
  trackTripDetailsDestinationRemoval,
  trackTripDetailsDestinationsChange,
  trackTripDetailsHover,
  trackTripDetailsTravellersChange,
} from './tracking';
import { TIOptionsValues } from './useTIOptions';
import { travelCompareQuotesParamsHash } from '$/(cms)/_modules/travelQuotesService/helpers';
import { CountryChipPicker } from '$/components/CountryChipPicker/CountryChipPicker';
import type { DestinationOption } from '$/components/CountryChipPicker/types';
import { DateRangePicker } from '$/components/DateRangePicker/DateRangePicker';
import type { UnavailableReason } from '$/components/DateRangePicker/unavailableReason';
import FormFieldLabel from '$/components/FormFieldLabel';
import NumberSelector from '$/components/NumberSelector/NumberSelector';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type DesktopTIOptionsProps = {
  searchParams: TravelCompareQuotesParams;
  onChangeParams: (value: TravelCompareQuotesParams) => void;
  countries: CmsCountry[];
  regions: CmsRegion[];
  boundaries: {
    minChildren?: number;
    maxChildren?: number;
    minAdult?: number;
    maxAdult?: number;
    maxTripDuration?: number;
    offsetStartDate?: number;
    tripType?: TravelInsuranceType;
    advancePurchaseDuration?: number;
    defaultAdult?: number;
    defaultChildren?: number;
    defaultTripDuration?: number;
    popularDestinations?: CmsCountry[];
    destinationCountries?: CmsCountry[];
    destinationRegion?: CmsRegion;
    excludeDestinations?: CmsCountry[];
  };
  values: TIOptionsValues;
  queryParamsReady: boolean;
};

export const DesktopTIOptions = (props: DesktopTIOptionsProps) => {
  const context = useClientContext();

  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const staticText = getClientTranslations(Translations, context);
  const { regions, values, queryParamsReady, searchParams, onChangeParams } =
    props;

  const [localValues, setLocalValues] = useState<
    Partial<TravelCompareQuotesParams>
  >({});

  // Reset on value change, not reference: poll ticks / re-parses churn the
  // searchParams reference without changing values, which would otherwise wipe
  // the user's in-progress edits (e.g. a cleared destination).
  const searchParamsHash = travelCompareQuotesParamsHash(searchParams);
  useEffect(() => {
    setLocalValues({});
  }, [searchParamsHash]);

  const {
    countryOptions,
    minDate,
    maxDuration,
    maxDate,
    minAdults,
    maxAdults,
    minChildren,
    maxChildren,
    countryMap,
    availableTripTypes,
    normalizeParams,
  } = values;

  // skipDestination keeps the raw destination so clearing it stays empty and
  // handlers spreading formParams don't re-add the default.
  const formParams: TravelCompareQuotesParams = normalizeParams(
    { ...searchParams, ...localValues },
    { skipDestination: true },
  );

  const isSingleTrip = formParams.insuranceType === 'SINGLE_PLAN';

  // Commit the latest form state at fire time. use-debounce always invokes the
  // most recent callback, so this closure sees current params — a destination
  // cleared after scheduling is seen here and skipped (no snapshot, no cancel).
  const scheduleCommit = useDebouncedCallback(() => {
    // Don't commit mid-selection (menu open), nor with an empty single-trip
    // destination (it would re-add the default via normalization and search).
    if (isCountryMenuOpen) {
      return;
    }
    if (isSingleTrip && !formParams.destinationIsoCodes?.length) {
      return;
    }
    onChangeParams(formParams);
  }, 350);

  const updateParams = (newValues: TravelCompareQuotesParams) => {
    setLocalValues(newValues);
    scheduleCommit();
  };

  const onDestinationMenuOpen = () => {
    setIsCountryMenuOpen(true);
  };

  const onDestinationMenuClose = useCallback(() => {
    setIsCountryMenuOpen(false);
    scheduleCommit();
  }, [scheduleCommit]);

  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [modalShown, setModalShown] = useState(true);

  const regionOption = useMemo(
    () => ({
      value: formParams.region || regions[0]?.value || '',
      label:
        regions.find((r) => r.value === formParams.region)?.label ||
        regions[0]?.label ||
        '',
    }),
    [formParams.region, regions],
  );
  const selectedCountries = useMemo<DestinationOption[]>(() => {
    if (!formParams.destinationIsoCodes) {
      return [];
    }

    return formParams.destinationIsoCodes
      .map((code) => ({
        value: code,
        label: countryMap.get(code) || code,
      }))
      .filter((opt) => opt.label !== opt.value);
  }, [formParams.destinationIsoCodes, countryMap]);

  const startDateObj = parseIsoDate(formParams.startDate);
  const endDateObj = parseIsoDate(formParams.endDate);

  const shouldShowModal =
    searchParams.noOfChildren &&
    !searchParams.travelAsFamily &&
    (searchParams.noOfAdults === 1 || searchParams.noOfAdults === 2);

  if (!shouldShowModal && modalShown && queryParamsReady) {
    setModalShown(false);
  }

  if (shouldShowModal && !modalShown && queryParamsReady) {
    setShowFamilyModal(true);
    setModalShown(true);
  }

  const handleTooltipHover = (label: string) => {
    trackTripDetailsHover(label);
  };

  const handleCoverTypeChange = (
    value: TravelCompareQuotesParams['insuranceType'],
  ) => {
    trackTripDetailsCoverType(
      value === 'SINGLE_PLAN' ? 'Single Trip' : 'Annual',
    );
    // Normalize without skipDestination so switching back to single re-applies
    // the default destination (a toggle isn't an explicit clear).
    const newValues = normalizeParams({
      ...formParams,
      insuranceType: value,
      endDate: '',
    });

    updateParams(newValues);
  };

  const handleSingleTripDestinationChange = (
    selected: DestinationOption[] | null,
  ) => {
    const codes = Array.isArray(selected)
      ? selected.map((opt) => opt.value)
      : [];
    const removedDestinations = selectedCountries.filter(
      (country) => !codes.includes(country.value),
    );
    const hasRemovedDestinations = removedDestinations.length > 0;
    if (hasRemovedDestinations) {
      trackTripDetailsDestinationRemoval(
        removedDestinations.map((country) => country.label).join(', '),
      );
    }
    const destinationLabel = codes
      .map((code) => countryMap.get(code) || code)
      .join(', ');
    if (!hasRemovedDestinations) {
      trackTripDetailsDestinationsChange(destinationLabel || '');
    }
    const newValues = {
      ...formParams,
      destinationIsoCodes: codes.length > 0 ? codes : undefined,
    };
    updateParams(newValues);
  };

  const handleRegionChange = (selected: DestinationOption | null) => {
    if (selected) {
      trackTripDetailsDestinationsChange(selected.label);
    }
    const newValues = {
      ...formParams,
      region: selected?.value,
    };

    updateParams(newValues);
  };

  // CMS-1853 discrete date events share market/brand/tripType params.
  const dateEventContext = {
    market: context.lang.split('-')[1]?.toUpperCase() ?? '',
    brand: context.brand,
    tripType: formParams.insuranceType ?? '',
    viewport: 'desktop' as const,
  };

  const handleDateSelectionStart = () => {
    trackTiDateSelectionStart(dateEventContext);
  };

  const handleDateUnavailableClick = (
    _date: Date,
    reason: UnavailableReason,
  ) => {
    trackTiDateUnavailableClick({ ...dateEventContext, reason });
  };

  const handleDateRangeSelect = (
    range: { from?: Date; to?: Date } | undefined,
  ) => {
    const newValues = { ...formParams };

    if (range?.from) {
      newValues.startDate = formatLocalIsoDate(range.from);
    }
    if (range?.to) {
      newValues.endDate = formatLocalIsoDate(range.to);
    }

    if (!range?.from || !range?.to) {
      // Clear dates if range is cleared
      newValues.startDate = '';
      newValues.endDate = '';
    }
    if (newValues.startDate && newValues.startDate !== formParams.startDate) {
      trackTripDetailsDateChange('start', newValues.startDate);
    }
    if (newValues.endDate && newValues.endDate !== formParams.endDate) {
      trackTripDetailsDateChange('end', newValues.endDate);
    }

    // Confirm commits the selection — fire the discrete confirm event with
    // the final range (dates computed at event time, not render time).
    if (range?.from && range?.to) {
      trackTiDateSelectionConfirm({
        ...dateEventContext,
        startDate: formatLocalIsoDate(range.from),
        endDate: formatLocalIsoDate(range.to),
        tripDurationDays: differenceInCalendarDays(range.to, range.from) + 1,
        advanceDays: differenceInCalendarDays(range.from, new Date()),
      });
    }

    updateParams(newValues);
  };

  const handleAdultChange = (value: number) => {
    const previousAdults = formParams.noOfAdults ?? 0;
    if (value !== previousAdults) {
      trackTripDetailsTravellersChange({
        travellerType: 'adult',
        action: value > previousAdults ? 'plus' : 'minus',
        value,
      });
    }
    const newLocalValues = {
      ...formParams,
      noOfAdults: value,
    };
    updateParams(newLocalValues);
  };

  const handleChildrenChange = (value: number) => {
    const previousChildren = formParams.noOfChildren ?? 0;
    if (value !== previousChildren) {
      trackTripDetailsTravellersChange({
        travellerType: 'child',
        action: value > previousChildren ? 'plus' : 'minus',
        value,
      });
    }
    const newLocalValues = {
      ...formParams,
      noOfChildren: value,
    };
    updateParams(newLocalValues);
  };

  const handleFamilyModalConfirm = () => {
    const newValues = {
      ...searchParams,
      travelAsFamily: true,
    };
    updateParams(newValues);
    setShowFamilyModal(false);
  };

  const handleFamilyModalCancel = () => {
    setShowFamilyModal(false);
  };

  return (
    <>
      <div className="hidden flex-wrap items-start gap-spacing/gap/soft-6px lg:flex">
        <CoverTypeToggle
          value={formParams.insuranceType}
          availableTripTypes={availableTripTypes}
          onChange={handleCoverTypeChange}
          onTooltipHover={handleTooltipHover}
        />

        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <div className="flex min-w-75 flex-[1_0_0] flex-col gap-spacing/gap/soft-6px">
          <FormFieldLabel
            label={staticText.destinationLabel}
            tooltip={staticText.destinationTooltip}
            onTooltipHover={() =>
              handleTooltipHover(staticText.destinationLabel)
            }
          />
          {isSingleTrip ? (
            <CountryChipPicker
              instanceId="ti-destination"
              isMulti
              mode="countries"
              maxVisibleValues={2}
              value={selectedCountries}
              onMenuOpen={onDestinationMenuOpen}
              onMenuClose={onDestinationMenuClose}
              options={countryOptions}
              onChange={(selected) =>
                handleSingleTripDestinationChange(
                  selected as DestinationOption[] | null,
                )
              }
            />
          ) : (
            <CountryChipPicker
              instanceId="ti-region"
              mode="select"
              value={regionOption}
              options={regions}
              onMenuOpen={onDestinationMenuOpen}
              onMenuClose={onDestinationMenuClose}
              onChange={(selected) =>
                handleRegionChange(selected as DestinationOption | null)
              }
            />
          )}
        </div>

        <div className="flex w-75 flex-col gap-spacing/gap/soft-6px">
          <FormFieldLabel
            label={staticText.travelDatesLabel}
            tooltip={staticText.travelDatesTooltip}
            onTooltipHover={() =>
              handleTooltipHover(staticText.travelDatesLabel)
            }
          />
          <DateRangePicker
            startDate={startDateObj}
            endDate={endDateObj}
            mode={isSingleTrip ? 'range' : 'annual'}
            onSelect={handleDateRangeSelect}
            onSelectionStart={handleDateSelectionStart}
            onUnavailableClick={handleDateUnavailableClick}
            minDate={minDate}
            maxDate={maxDate}
            duration={maxDuration}
          />
        </div>

        <div className="w-25">
          <NumberSelector
            value={formParams.noOfAdults}
            onChange={handleAdultChange}
            minValue={minAdults}
            maxValue={maxAdults}
            step={1}
            label={staticText.adultsLabel}
            tooltip={staticText.adultsTooltip}
            id="ti-adults"
            onTooltipHover={() => handleTooltipHover(staticText.adultsLabel)}
          />
        </div>

        <div className="w-25">
          <NumberSelector
            value={formParams.noOfChildren}
            onChange={handleChildrenChange}
            minValue={minChildren}
            maxValue={maxChildren}
            step={1}
            label={staticText.childrenLabel}
            tooltip={staticText.childrenTooltip}
            id="ti-children"
            onTooltipHover={() => handleTooltipHover(staticText.childrenLabel)}
          />
        </div>
      </div>

      <FamilyTravelModal
        isOpen={showFamilyModal}
        onConfirm={handleFamilyModalConfirm}
        onCancel={handleFamilyModalCancel}
      />
    </>
  );
};
